
import React from 'react';
import SEO from '../components/SEO';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO 
        title="Privacy Policy" 
        description="AviaGov data protection policy and privacy standards for aviation authorities." 
      />
      <section className="py-24 px-4 max-w-4xl mx-auto">
        <h1 className="text-4xl lg:text-6xl font-bold font-heading mb-8 text-white">Privacy Policy</h1>
        <p className="text-slate-400 mb-12">Last Updated: May 22, 2026</p>

        <div className="prose prose-invert prose-slate max-w-none space-y-8 text-slate-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p>
              At AviaGov, we are committed to protecting the privacy and security of our users' data, particularly the mission-critical aeronautical information managed through our platform. This policy outlines how AviaGov Aviation Systems Ltd ("AviaGov", "we", "us", or "our") collects, uses, and protects information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
            <p>We collect information in several ways:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Professional Contact Information:</strong> Names, emails, and organizational details provided via demo requests.</li>
              <li><strong>FlyMate Introduction Data:</strong> Names, surnames, work emails, organization names, optional role/job-title details, and the required combined acceptance covering Terms of Service, Privacy Policy, and AviaGov email updates/marketing communications submitted to start a FlyMate session.</li>
              <li><strong>FlyMate Usage Records:</strong> Questions submitted through FlyMate, answers returned, token and request usage, associated lead or account information, and country/city details when resolvable from request context.</li>
              <li><strong>FlyMate Uploaded Files:</strong> Files submitted by paid FlyMate users, extracted text from those files, file type, size, and attachment metadata needed to process the request. Raw filenames may be sanitized or replaced before being sent to AI workflows.</li>
              <li><strong>Technical Logs:</strong> Metadata regarding platform usage, IP addresses, and session identifiers for security auditing.</li>
              <li><strong>Aeronautical Data:</strong> As a processor, we handle data provided by Civil Aviation Authorities (CAAs) and ANSPs, which is governed by specific Data Processing Agreements (DPAs).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Use of Information</h2>
            <p>
              Information collected is used solely to provide, maintain, and improve the AviaGov platform services. This includes ensuring ICAO Annex compliance, facilitating AIRAC cycles, and providing technical support.
            </p>
            <p>
              For FlyMate specifically, this includes opening the user session, following up with prospective customers, understanding product usage, monitoring grounded-answer quality and safety, processing uploaded files, extracting file text, preparing AI context, generating answers or draft artifacts, and estimating usage.
            </p>
            <p>
              FlyMate content, including uploaded-file text and prompts, may be processed by configured AI service providers and automation providers solely to provide and secure the requested service. Users should avoid uploading unnecessary personal data, classified information, export-controlled information, state secrets, or security-sensitive aviation data unless a separate written agreement expressly authorizes that processing.
            </p>
            <p>
              For FlyMate, the required combined acceptance is also used to authorize AviaGov follow-up communications, including email updates, marketing messages, and product updates.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Data Sovereignty & Security</h2>
            <p>
              Given the nature of national aviation data, AviaGov provides flexible deployment models including dedicated cloud instances and on-premise solutions to ensure compliance with national data sovereignty laws.
            </p>
            <p>
              FlyMate uploaded files are handled with security controls appropriate for the service tier and deployment model. AviaGov may limit retention, access, or processing of uploaded files and extracted text according to operational, security, legal, and contractual requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. GDPR, Account Deletion, and Retention</h2>
            <p>
              Where applicable, users may exercise data protection rights under GDPR or similar privacy laws, including requesting access, correction, restriction, portability, objection, or deletion of personal data.
            </p>
            <p>
              To request deletion of a FlyMate or AviaGov account, the user must email <strong>support@aviagov.com</strong> from the account email address and clearly request account deletion. AviaGov may require identity verification before processing the request.
            </p>
            <p>
              Deletion requests are subject to legal, contractual, security, billing, fraud-prevention, audit, dispute-resolution, and legitimate business retention requirements. AviaGov may retain records for as long as reasonably necessary to comply with those requirements, protect the platform, enforce agreements, resolve disputes, and maintain required operational or compliance evidence.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Contact Us</h2>
            <p>
              For questions regarding this policy, contact our Data Protection Officer at <strong>privacy@flyclim.aero</strong>. For account deletion requests, email <strong>support@aviagov.com</strong>.
            </p>
          </section>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
